import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Save, ShieldOff, TimerReset } from "lucide-react";
import { api, apiError, inr, logError } from "@/lib/api";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Empty } from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export const SalaryRules = () => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/salary-rules/settings").then(({ data }) => { setSettings(data); setForm(data); })
      .catch((e) => logError("salary rules", e));
    api.get("/salary-rules/employees").then(({ data }) => setRows(data.employees))
      .catch((e) => logError("employee salary rules", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!settings || !form) return null;

  const saveGlobal = async () => {
    setBusy("global");
    try {
      const { data } = await api.put("/salary-rules/settings", {
        default_method: form.default_method,
        default_shift_hours: Number(form.default_shift_hours),
        default_ot_multiplier: Number(form.default_ot_multiplier),
        salary_visibility_global: !!form.salary_visibility_global,
        ot_limits: form.ot_limits,
        time_loss: form.time_loss,
        shift_hours_by_department: form.shift_hours_by_department || {},
        shift_hours_by_designation: form.shift_hours_by_designation || {},
      });
      setSettings(data);
      setForm(data);
      toast.success("Salary rules saved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const saveEmployee = async (row, changes) => {
    setBusy(row.user_id);
    try {
      const { data } = await api.put(`/salary-rules/employees/${row.user_id}`, changes);
      setRows((rs) => rs.map((r) => (r.user_id === row.user_id ? { ...r, ...data } : r)));
      toast.success(`${row.name} updated`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const tl = form.time_loss || {};
  const ot = form.ot_limits || {};

  return (
    <div className="space-y-4" data-testid="salary-rules">
      <div className="surface space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Default salary method">
            <Select value={form.default_method}
              onValueChange={(v) => setForm({ ...form, default_method: v })}>
              <SelectTrigger data-testid="rules-default-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {settings.methods.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Default shift hours">
            <Select value={String(form.default_shift_hours)}
              onValueChange={(v) => setForm({ ...form, default_shift_hours: v })}>
              <SelectTrigger data-testid="rules-default-hours"><SelectValue /></SelectTrigger>
              <SelectContent>
                {settings.shift_hour_choices.map((h) => (
                  <SelectItem key={h} value={String(h)}>{h} hours</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Default OT multiplier">
            <Select value={String(form.default_ot_multiplier)}
              onValueChange={(v) => setForm({ ...form, default_ot_multiplier: v })}>
              <SelectTrigger data-testid="rules-default-ot"><SelectValue /></SelectTrigger>
              <SelectContent>
                {settings.ot_multipliers.map((m) => (
                  <SelectItem key={m} value={String(m)}>{m}×</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Salary visible to employees (global)">
            <div className="pt-2">
              <Switch checked={!!form.salary_visibility_global} data-testid="rules-visibility-global"
                onCheckedChange={(v) => setForm({ ...form, salary_visibility_global: v })} />
            </div>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          {[["daily_hours", "Max OT hours / day"], ["weekly_hours", "Max OT hours / week"],
            ["monthly_hours", "Max OT hours / month"], ["max_shifts", "Max OT shifts / month"]].map(
            ([key, label]) => (
              <Field key={key} label={label}>
                <Input type="number" data-testid={`rules-ot-${key}`} value={ot[key] ?? 0}
                  onChange={(e) => setForm({ ...form, ot_limits: { ...ot, [key]: Number(e.target.value) } })} />
              </Field>
            )
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Late grace (minutes)">
            <Input type="number" data-testid="rules-tl-grace" value={tl.grace_minutes ?? 15}
              onChange={(e) => setForm({ ...form, time_loss: { ...tl, grace_minutes: Number(e.target.value) } })} />
          </Field>
          <Field label="Lunch allowed (minutes)">
            <Input type="number" data-testid="rules-tl-lunch" value={tl.lunch_minutes ?? 45}
              onChange={(e) => setForm({ ...form, time_loss: { ...tl, lunch_minutes: Number(e.target.value) } })} />
          </Field>
          <Field label="Half-day time loss (hours)">
            <Input type="number" data-testid="rules-tl-half" value={tl.half_day_hours ?? 4}
              onChange={(e) => setForm({ ...form, time_loss: { ...tl, half_day_hours: Number(e.target.value) } })} />
          </Field>
          <Field label="Full-day time loss (hours)">
            <Input type="number" data-testid="rules-tl-full" value={tl.full_day_hours ?? 8}
              onChange={(e) => setForm({ ...form, time_loss: { ...tl, full_day_hours: Number(e.target.value) } })} />
          </Field>
          <Field label="Time loss detection on">
            <div className="pt-2">
              <Switch checked={tl.enabled !== false} data-testid="rules-tl-enabled"
                onCheckedChange={(v) => setForm({ ...form, time_loss: { ...tl, enabled: v } })} />
            </div>
          </Field>
          <Field label="Deduct approved time loss in payroll">
            <div className="pt-2">
              <Switch checked={!!tl.deduct_enabled} data-testid="rules-tl-deduct"
                onCheckedChange={(v) => setForm({ ...form, time_loss: { ...tl, deduct_enabled: v } })} />
            </div>
          </Field>
        </div>

        <p className="text-xs text-muted-foreground">
          Time loss is only ever <b>detected and suggested</b>. Nothing is deducted until you approve
          it under Time loss. Employees never see methods, multipliers or thresholds.
        </p>
        <Button size="sm" className="gap-1.5" onClick={saveGlobal} disabled={busy === "global"}
          data-testid="rules-save">
          {busy === "global" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save rules
        </Button>
      </div>

      <div className="surface overflow-x-auto" data-testid="salary-rules-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Salary method</TableHead>
              <TableHead>Shift hours</TableHead>
              <TableHead>OT ×</TableHead>
              <TableHead className="text-right">Day / hour rate</TableHead>
              <TableHead className="text-right">Salary visible</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.user_id} data-testid={`salary-rule-row-${r.user_id}`}>
                <TableCell className="text-sm font-medium">
                  {r.name}
                  <div className="text-xs text-muted-foreground">
                    {r.employee_code} • {r.department || "—"}
                  </div>
                </TableCell>
                <TableCell>
                  <Select value={r.method}
                    onValueChange={(method) => saveEmployee(r, { method })}>
                    <SelectTrigger className="h-8 w-56 text-xs" data-testid={`salary-method-${r.user_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {settings.methods.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select value={String(r.shift_hours)}
                    onValueChange={(v) => saveEmployee(r, { shift_hours: Number(v) })}>
                    <SelectTrigger className="h-8 w-24 text-xs" data-testid={`salary-hours-${r.user_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {settings.shift_hour_choices.map((h) => (
                        <SelectItem key={h} value={String(h)}>{h} h</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select value={String(r.ot_multiplier)}
                    onValueChange={(v) => saveEmployee(r, { ot_multiplier: Number(v) })}>
                    <SelectTrigger className="h-8 w-20 text-xs" data-testid={`salary-ot-${r.user_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {settings.ot_multipliers.map((m) => (
                        <SelectItem key={m} value={String(m)}>{m}×</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right font-mono-num text-xs">
                  {inr(r.daily_rate)} / {inr(r.hourly_rate)}
                  <div className="text-[10px] text-muted-foreground">{r.period_days} day basis</div>
                </TableCell>
                <TableCell className="text-right">
                  <Select
                    value={r.salary_visible === null || r.salary_visible === undefined
                      ? "inherit" : r.salary_visible ? "on" : "off"}
                    onValueChange={(v) =>
                      saveEmployee(r, { salary_visible: v === "inherit" ? null : v === "on" })}>
                    <SelectTrigger className="ml-auto h-8 w-36 text-xs"
                      data-testid={`salary-visibility-${r.user_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Follow global</SelectItem>
                      <SelectItem value="on">Visible</SelectItem>
                      <SelectItem value="off">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {r.effective_visibility ? "employee sees salary" : "salary hidden"}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export const TimeLossReview = () => {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [days, setDays] = useState({});

  const load = useCallback(() => {
    api.get(`/time-loss?month=${month}`).then(({ data: d }) => setData(d))
      .catch((e) => logError("time loss", e));
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, decision) => {
    setBusy(row.user_id + decision);
    try {
      await api.post("/time-loss/decide", {
        user_id: row.user_id, month, decision,
        days: decision === "partial" ? Number(days[row.user_id] || 0) : undefined,
      });
      toast.success(`Time loss ${decision}d for ${row.name}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="time-loss-review">
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Month">
          <Input type="month" className="w-40" value={month} data-testid="time-loss-month"
            onChange={(e) => setMonth(e.target.value)} />
        </Field>
        <Badge variant="outline" className="mb-2 text-xs" data-testid="time-loss-pending">
          {data.pending} awaiting your decision
        </Badge>
      </div>

      {data.rows.length === 0 ? (
        <Empty title="No time loss detected this month" hint="Late arrivals, early check-outs, late lunch returns and long breaks will appear here" />
      ) : (
        data.rows.map((r) => (
          <div key={r.user_id} className="surface space-y-2 p-4" data-testid={`time-loss-row-${r.user_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <TimerReset className="h-4 w-4 text-primary" /> {r.name}
                  <span className="text-xs text-muted-foreground">{r.employee_code}</span>
                  <Badge variant={r.decision ? "outline" : "default"} className="text-[10px]"
                    data-testid={`time-loss-status-${r.user_id}`}>
                    {r.decision ? `${r.decision.decision} • ${r.decision.approved_days} day(s)` : "Pending review"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {r.total_hours}h total • late {r.late_minutes}m • early out {r.early_minutes}m •
                  lunch {r.lunch_late_minutes}m • break {r.break_excess_minutes}m •
                  suggested deduction <b>{r.suggested_days} day(s)</b>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input type="number" step="0.5" className="h-8 w-20" placeholder="days"
                  data-testid={`time-loss-days-${r.user_id}`}
                  value={days[r.user_id] ?? ""}
                  onChange={(e) => setDays({ ...days, [r.user_id]: e.target.value })} />
                <Button size="sm" className="gap-1" disabled={!!busy}
                  data-testid={`time-loss-approve-${r.user_id}`} onClick={() => decide(r, "approve")}>
                  <Check className="h-3.5 w-3.5" /> Approve
                </Button>
                <Button size="sm" variant="outline" disabled={!!busy}
                  data-testid={`time-loss-partial-${r.user_id}`} onClick={() => decide(r, "partial")}>
                  Partial
                </Button>
                <Button size="sm" variant="outline" disabled={!!busy}
                  data-testid={`time-loss-waive-${r.user_id}`} onClick={() => decide(r, "waive")}>
                  <ShieldOff className="h-3.5 w-3.5" /> Waive
                </Button>
                <Button size="sm" variant="destructive" disabled={!!busy}
                  data-testid={`time-loss-reject-${r.user_id}`} onClick={() => decide(r, "reject")}>
                  Reject
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {r.days.slice(0, 12).map((d) => (
                <Badge key={d.date} variant="outline" className="text-[10px]">
                  {d.date.slice(8)} • {d.total_minutes}m
                </Badge>
              ))}
            </div>
          </div>
        ))
      )}
      <p className="text-xs text-muted-foreground">
        Nothing is deducted automatically. Your decision here is what payroll uses, and every
        decision is written to the audit log.
      </p>
    </div>
  );
};
